import { Component, Input } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { ControlService } from '../library/control.service';
import { CallService } from 'src/app/services/learner/call.service';
import { AppConstants } from 'src/app/app-constants';
import { AmplitudeService } from 'src/app/services/external-tools/amplitude.service';
import { AmplitudeTrackingEvents } from 'src/app/utils/enums/amplitude/amplitudeTrackingEvents.enum';
import { AmplitudeTrackingEventPropValues } from 'src/app/utils/enums/amplitude/amplitudeTrackingEventPropValues.enum';
import { HotjarService } from 'src/app/services/external-tools/hotjar.service';
import { PlayerService } from '../library/player.service';
import { CallStoreService } from '../library/call-store/call-store.service';
import { TranslocoService } from '@jsverse/transloco';
import { LoggerService } from 'src/app/services/logger/logger.service';

@Component({
  selector: 'app-post-call',
  templateUrl: './post-call.component.html',
  styleUrls: ['./post-call.component.scss'],
  standalone: false,
})
export class PostCallComponent {
  @Input() controlService: ControlService | undefined;
  @Input() playerService: PlayerService | undefined;
  isNative = Capacitor.isNativePlatform();
  callGuided: string = AppConstants.CALL_TYPE_GUIDED;
  callConversational: string = AppConstants.CALL_TYPE_CONVERSATIONAL;
  callType: string | undefined = '';
  practiceCallUuid: string = '';
  feedbackSubmitted: boolean = false;
  selectedFeedback: boolean | null = null;
  isRealtime: boolean = false;
  isCompleted: boolean = false;
  isPersonalizedCall: boolean = false;

  // Text display
  title: string = '';
  titleNormal: string = this.translocoService.translate(
    'CALL_ENDED_MESSAGE',
    {},
    'call'
  );
  titleInactivity: string = this.translocoService.translate(
    'CALL_ENDED_INACTIVITY',
    {},
    'call'
  );
  titleCallEnded: string = this.translocoService.translate(
    'CALL_ENDED_GENERIC',
    {},
    'call'
  );

  subtitle: string = '';
  subtitleNormal: string = this.translocoService.translate(
    'FEEDBACK_PROMPT',
    {},
    'call'
  );
  subtitleNotMeet: string = this.translocoService.translate(
    'INTERACTION_THRESHOLD_NOT_MET',
    {},
    'call'
  );

  question: string = '';
  questionConversational: string = this.translocoService.translate(
    'REALISTIC_CONVERSATION_QUESTION',
    {},
    'call'
  );
  questionGuided: string = this.translocoService.translate(
    'KNOWLEDGEABLE_PREPARED_QUESTION',
    {},
    'call'
  );

  thankYouMessage: string = '';
  thankYouMessagePositive: string = this.translocoService.translate(
    'FEEDBACK_THANK_YOU',
    {},
    'call'
  );
  thankYouMessageNegative: string = this.translocoService.translate(
    'FEEDBACK_IMPROVEMENT_NOTE',
    {},
    'call'
  );

  constructor(
    private callService: CallService,
    private amplitudeService: AmplitudeService,
    private hotjarService: HotjarService,
    private callStore: CallStoreService,
    private translocoService: TranslocoService,
    private logger: LoggerService
  ) {}

  ngOnInit() {
    this.isRealtime = !!this.controlService?.isRealTimeCall;
    this.isCompleted = !!this.callStore.getIsCallComplete();
    this.isPersonalizedCall = this.callStore.getIsPersonalizedCall();
    const userInactive = !!this.controlService?.userInactivity;
    this.callType = this.controlService?.callType;

    // set the title based on the call type
    this.title = !this.isRealtime
    ? this.titleNormal
    : this.isCompleted || !userInactive
    ? this.titleCallEnded
    : this.titleInactivity;

    // set the subtitle based on the call type
    this.subtitle = !this.isRealtime
    ? this.subtitleNormal
    : this.isCompleted
    ? this.subtitleNormal
    : this.subtitleNotMeet;

    this.logger.log(
      `[CALL] end call screen - isRealtime [${this.isRealtime}] isCompleted [${this.isCompleted}] isPersonalizedCall [${this.isPersonalizedCall}] title [${this.title}] subtitle [${this.subtitle}]`,
    );
    // set the question based on the call type
    if (this.callType === this.callGuided) {
      this.question = this.questionGuided;
    } else {
      this.question = this.questionConversational;
    }

    this.practiceCallUuid = this.controlService?.practiceCallUuid || '';
    this.selectedFeedback = null;

    this.logger.log('[CALL] end call screen - init', {
      isCompleted: this.isCompleted,
      userInactivity: userInactive,
      title: this.title,
      subtitle: this.subtitle,
      question: this.question,
    });

    // Sending the Event of Landing on End Call Page to Amplitude
    this.amplitudeService.trackEvent(
      AmplitudeTrackingEvents.END_CALL_PAGEVIEW,
      {
        call_type: this.controlService?.callType,
        call_uuid: this.controlService?.callUuid,
        practice_call_uuid: this.practiceCallUuid,
        is_personalized_call: this.isPersonalizedCall,
      }
    );
  }

  submitFeedback(feedback: boolean): void {
    this.thankYouMessage = feedback
      ? this.thankYouMessagePositive
      : this.thankYouMessageNegative;
    // Sending the Event of Clicking on End Call Feedback to Amplitude
    this.amplitudeService.trackEvent(
      AmplitudeTrackingEvents.END_CALL_CLICK_FEEDBACK,
      {
        thumbs_type: feedback
          ? AmplitudeTrackingEventPropValues.FEEDBACK_THUMBS_UP
          : AmplitudeTrackingEventPropValues.FEEDBACK_THUMBS_DOWN,
        call_type: this.controlService?.callType,
        call_completed: this.isCompleted,
      }
    );

    this.selectedFeedback = feedback;
    this.feedbackSubmitted = true;

    this.logger.log('[CALL] end call screen - click feedback', {
      isPositive: feedback,
    });

    this.callService.submitFeedback(this.practiceCallUuid, feedback).subscribe({
      next: () => {
        console.log(
          'Response received successfully on submitting feedback at the end of the call'
        );
      },
      error: (err) => {
        this.logger.error('[CALL] end call screen - submit feedback error', err);
      },
    });

    // Open Hotjar feedback modal when user clicks on dislike
    if (!feedback) {
      const accountUuid = localStorage.getItem('current_account_uuid') || '-';
      const attributes = {
        accountUuid,
        callType: this.callType,
        practiceCallUuid: this.practiceCallUuid,
        moduleAssignmentUuid: this.callStore.getModuleAssignmentUuid(),
        callUuid: this.callStore.getCallUuid(),
        triggeredBy: 'PostCallComponent',
      };
      this.hotjarService.openSurveyByEvent(
        'practice_feedback_dislike',
        attributes
      );

      this.logger.log('[CALL] end call screen - open survey', attributes);
    }
  }

  clickErrorModalOK(): void {
    this.amplitudeService.trackEvent(
      AmplitudeTrackingEvents.END_CALL_ERROR_MODAL_OK,
      {
        call_type: this.controlService?.callType,
        call_uuid: this.controlService?.callUuid,
        practice_call_uuid: this.practiceCallUuid,
      }
    );

    this.controlService?.sendRequestToEndCall();

    const isNetworkError = !this.navigatorOnline;
    const isEndCallError = !this.isCompleted;

    this.logger.log('[CALL] end call screen - click error modal OK', {
      isNetworkError: isNetworkError,
      isEndCallError: isEndCallError,
    });
  }

  clickErrorModalReturnAssignment(): void {
    this.amplitudeService.trackEvent(
      AmplitudeTrackingEvents.END_CALL_ERROR_MODAL_ASSIGNMENT,
      {
        call_type: this.controlService?.callType,
        call_uuid: this.controlService?.callUuid,
        practice_call_uuid: this.practiceCallUuid,
      }
    );
    this.controlService?.goBackToAssigment();

    const isNetworkError = !this.navigatorOnline;
    const isEndCallError = !this.isCompleted;

    this.logger.log(
      '[CALL] end call screen - click error modal return assignment',
      {
        isNetworkError: isNetworkError,
        isEndCallError: isEndCallError,
      }
    );
  }

  protected get navigatorOnline(): boolean {
    const navigator = window.navigator;
    const isOnline = navigator.onLine;
    return isOnline;
  }

  async viewResults() {
    if (
      this.controlService?.isVisualAidCall &&
      this.callStore.getIsSendingAnswer()
    ) {
      this.logger.log(
        '[CALL] end call screen - click error view results wait for answer to send'
      );

      await this.waitForAnswerToSend();
    }
    this.controlService?.viewResultsPage();

    this.logger.log('[CALL] end call screen - click view results page');
  }

  private waitForAnswerToSend(): Promise<void> {
    return new Promise((resolve) => {
      const check = () => {
        if (!this.callStore.getIsSendingAnswer()) {
          resolve();
        } else {
          setTimeout(check, 50);
        }
      };
      check();
    });
  }
}
